import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AiAssistant } from '../components/AiAssistant'
import { Icon } from '../components/Icon'
import { ModuleFrame } from '../components/ModuleFrame'
import { MODULES, MODULE_BY_ID } from '../data/catalog'
import { useProgress } from '../state/ProgressContext'

export function ModulePage() {
  const { moduleId } = useParams()
  const module = moduleId ? MODULE_BY_ID.get(moduleId.toUpperCase()) : undefined
  const location = useLocation()
  const navigate = useNavigate()
  const { completeSection, recordPosition, isSectionComplete, completedForModule } = useProgress()
  const requestedSection = location.hash.replace(/^#/, '')
  const initialIndex = module && requestedSection ? module.sections.findIndex((section) => section.sectionId === requestedSection) : 0
  const [activeIndex, setActiveIndex] = useState(Math.max(0, initialIndex))
  const [sectionRequest, setSectionRequest] = useState(Math.max(0, initialIndex))
  const [sectionText, setSectionText] = useState('')
  const resumeHelp = new URLSearchParams(location.search).get('ai') === '1'
  const [helpOpen, setHelpOpen] = useState(resumeHelp)
  const [helpRequest, setHelpRequest] = useState(resumeHelp ? 1 : 0)

  useEffect(() => {
    if (!module) return
    const next = requestedSection ? module.sections.findIndex((section) => section.sectionId === requestedSection) : 0
    if (next >= 0) {
      setSectionText('')
      setActiveIndex(next)
      setSectionRequest(next)
    }
  }, [module?.id, requestedSection])

  const moduleIndex = module ? MODULES.findIndex((item) => item.id === module.id) : -1
  const previous = moduleIndex > 0 ? MODULES[moduleIndex - 1] : null
  const next = moduleIndex >= 0 && moduleIndex < MODULES.length - 1 ? MODULES[moduleIndex + 1] : null
  const section = module?.sections[activeIndex]
  const completed = module ? completedForModule(module.id) : 0
  const percent = module ? Math.round((completed / module.sections.length) * 100) : 0

  const onSectionChange = useCallback((index: number) => {
    if (!module) return
    const nextSection = module.sections[index]
    if (!nextSection) return
    setSectionText('')
    setActiveIndex(index)
    window.history.replaceState(window.history.state, '', `/modules/${module.id}#${nextSection.sectionId}`)
    recordPosition(module.courseId, module.id, nextSection.sectionId)
  }, [module, recordPosition])

  const onSectionTextChange = useCallback((text: string) => setSectionText(text), [])

  const assistantContext = useMemo(() => module && section ? {
    courseId: module.courseId,
    moduleId: module.id,
    sectionId: section.sectionId,
    sectionLabel: section.label,
    sectionText,
  } : {}, [module, section, sectionText])

  if (!module || !section) return <Navigate to="/" replace />
  return (
    <div className="module-page">
      <div className="module-topbar page-width">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link><Icon name="chevron" size={14} />
          <Link to={`/courses/${module.courseId}`}>{module.courseTitle}</Link><Icon name="chevron" size={14} />
          <span>{module.id}</span>
        </nav>
        <div className="module-nav">
          {previous ? <Link to={`/modules/${previous.id}`} title={previous.title}>← {previous.id}</Link> : <span />}
          <span>{moduleIndex + 1} of {MODULES.length}</span>
          {next ? <Link to={`/modules/${next.id}`} title={next.title}>{next.id} →</Link> : <Link to="/">Home →</Link>}
        </div>
      </div>
      <section className="module-heading page-width">
        <div>
          <span className="eyebrow">{module.courseTitle} · {module.id}</span>
          <h1>{module.title}</h1>
          <p>{module.summary}</p>
        </div>
        <div className="module-completion">
          <strong>{percent}%</strong><span>of this module complete</span>
          <div><i style={{ width: `${percent}%` }} /></div>
        </div>
      </section>
      <div className="module-workspace page-width">
        <aside className="section-rail" aria-label="Module sections">
          <strong>In this module</strong>
          <div className="section-select-mobile">
            <label htmlFor="module-section">Section</label>
            <select id="module-section" value={activeIndex} onChange={(event) => setSectionRequest(Number(event.target.value))}>
              {module.sections.map((item, index) => <option key={item.sectionId} value={index}>{index + 1}. {item.label}</option>)}
            </select>
          </div>
          <ol>
            {module.sections.map((item, index) => {
              const done = isSectionComplete(module.id, item.sectionId)
              return (
                <li key={item.sectionId}>
                  <button type="button" className={index === activeIndex ? 'active' : ''} onClick={() => setSectionRequest(index)} aria-current={index === activeIndex ? 'step' : undefined}>
                    <span>{done ? <Icon name="check" size={14} /> : index + 1}</span>
                    <b>{item.label}</b>
                  </button>
                </li>
              )
            })}
          </ol>
          <Link className="rail-course-link" to={`/courses/${module.courseId}`}>View course outline</Link>
        </aside>
        <section className="lesson-area">
          <div className="section-actions">
            <div><span>Section {activeIndex + 1} of {module.sections.length}</span><strong>{section.label}</strong></div>
            <div>
              <button className="button" type="button" disabled={!sectionText} onClick={() => { setHelpOpen(true); setHelpRequest((value) => value + 1); window.setTimeout(() => document.getElementById('ai-help')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0) }}><Icon name="ai" size={16} />Explain this</button>
              <button
                className={`button ${isSectionComplete(module.id, section.sectionId) ? 'complete' : 'primary'}`}
                type="button"
                disabled={isSectionComplete(module.id, section.sectionId)}
                onClick={() => completeSection(module.courseId, module.id, section.sectionId)}
              >
                <Icon name="check" size={16} />{isSectionComplete(module.id, section.sectionId) ? 'Completed' : 'Mark complete'}
              </button>
            </div>
          </div>
          <AiAssistant key={`${module.id}:${section.sectionId}`} context={assistantContext} expanded={helpOpen} onExpandedChange={setHelpOpen} starterRequest={helpRequest} starterPrompt="Explain this section in simpler language and give one practical example." />
          <ModuleFrame
            module={module}
            requestedSectionId={requestedSection}
            sectionRequest={sectionRequest}
            onSectionChange={onSectionChange}
            onSectionTextChange={onSectionTextChange}
            onQuizPassed={() => {
              const check = module.sections.find((item) => item.sectionId === 'check-yourself')
              if (check) completeSection(module.courseId, module.id, check.sectionId)
            }}
          />
          <div className="lesson-step">
            <button className="button" type="button" disabled={activeIndex === 0} onClick={() => setSectionRequest(Math.max(0, activeIndex - 1))}>← Previous section</button>
            {activeIndex < module.sections.length - 1 ? (
              <button className="button primary" type="button" onClick={() => setSectionRequest(activeIndex + 1)}>Next section →</button>
            ) : next ? <button className="button primary" type="button" onClick={() => navigate(`/modules/${next.id}`)}>Next module: {next.id} →</button> : <button className="button primary" type="button" onClick={() => navigate('/')}>Return home</button>}
          </div>
        </section>
      </div>
    </div>
  )
}
